/**
 * The ai-activity-inbox-api v4 contract, mirroring the Pydantic models in
 * `ai_activity_inbox_api/schemas.py`. Kept as hand-written interfaces rather
 * than generated from the OpenAPI fragment because the fragment describes the
 * subset of the schema published to api-internal, not the full response body.
 *
 * Keep in step with `ACTIVITY_SCHEMA_VERSION` on the backend. v3 moved
 * `disposition` and `rationale` onto each action and made the proposal-level
 * `disposition` a derived roll-up of them; v4 renamed `Citation.quote` to
 * `part_body` and split the router agent identity into UUIDs plus a label.
 */

export type ActivityStatus =
  | 'needs_confirmation'
  | 'in_progress'
  | 'done'
  | 'error'
  | 'abandoned'
  | 'ran_automatically';

/** `undefined` is a real task with no typed Care Plan executor (a follow-up
 * call, a research task) — not "nothing to do". A window with nothing
 * actionable comes back as an empty `actions` array instead.
 *
 * These are the known types, not a closed set: the API types `action_type` as
 * an open string so an operator can add one to the rtl-policy table without a
 * deploy. Anything unrecognised is rewritten to `undefined` by the API's
 * create-time clamp, so in practice only these reach the UI — but do not index
 * a map with `action_type` without a fallback (see `actionTypeMeta`). */
export type ActionType =
  | 'create_case'
  | 'progress_option_status'
  | 'create_outcome'
  | 'calculate_ati'
  | 'create_action'
  | 'progress_case_status'
  | 'update_profile'
  | 'capture_data'
  | 'send_nps_survey'
  | 'send_navi_message'
  | 'send_aifa_message'
  | 'broadcast_notification'
  | 'send_email'
  | 'send_slack_message'
  | 'send_sms'
  | 'create_jira_ticket'
  | 'undefined';

/** `auto_deny` is proposal-level only on an action card: the inbox drops
 * policy-denied types rather than stamping this value on them. An empty
 * `actions` array also derives it. The backend rejects it on an individual
 * action. */
export type Disposition = 'auto_execute' | 'needs_approval' | 'auto_deny';

export interface Citation {
  part_id: string;
  /** The agent's excerpt of the cited message — not the whole message, which
   * is `MessagePart.body_preview`. Null on activities created before the v4
   * rename, whose stored `quote` the backend no longer reads. */
  part_body?: string | null;
}

export interface ProposedAction {
  /** Widened past `ActionType` on purpose — see that type's note. */
  action_type: ActionType | (string & {});
  title: string;
  instruction: string;
  disposition: Disposition;
  rationale: string;
}

export interface RouterProposal {
  title: string;
  description: string;
  actions: ProposedAction[];
  citations: Citation[];
  disposition: Disposition;
}

export interface MessagePart {
  part_id: string;
  author_type: string;
  author_id?: string | null;
  created_at: string;
  body_preview: string;
  part_type: string;
}

export interface EnrichedConversationEvent {
  schema_version: string;
  flush_id: string;
  conversation_id: string;
  client_guid?: string | null;
  channel: string;
  message_count: number;
  window_start: string;
  window_end: string;
  message_parts: MessagePart[];
}

export interface ActivityNote {
  note: string;
  actor: string;
  created_at: string;
}

export interface Activity {
  schema_version: string;
  activity_id: string;
  client_guid?: string | null;
  intercom_conversation_id: string;
  intercom_contact_id?: string | null;
  status: ActivityStatus;
  title: string;
  description: string;
  conversation_link?: string | null;
  /** Router identity, all rendered in the Debug panel. `router_agent_id` is
   * Command Center's identifier `namespace:agent_name:alias`.
   * `router_agent_version_id` is Command Center's version UUID — search on
   * that. `router_agent_label` is the readable "name-vN" and is the one to
   * show a human. To find the run behind a proposal, search on `activity_id`:
   * it is the window's flush_id, which the router logs on every line. */
  router_agent_id?: string | null;
  router_agent_version_id?: string | null;
  router_agent_label?: string | null;
  router_proposal: RouterProposal;
  enriched_event: EnrichedConversationEvent;
  notes: ActivityNote[];
  created_at: string;
  updated_at: string;
}

export interface CursorPage<T> {
  items: T[];
  next_cursor: string | null;
  previous_cursor: string | null;
  has_more: boolean;
}

export type ReviewAction = 'confirm' | 'retry' | 'deny';

/** Chip styling and copy for each status. `summaryLabel` is the wording used in
 * the count line above the filters, which reads as a sentence rather than as
 * chip labels ("5 awaiting review", not "5 needs confirmation"). */
export interface StatusMeta {
  label: string;
  summaryLabel: string;
  chipClass: string;
  dotClass: string;
}

export const STATUS_META: Record<ActivityStatus, StatusMeta> = {
  needs_confirmation: {
    label: 'Needs confirmation',
    summaryLabel: 'awaiting review',
    chipClass: 'bg-amber-50 text-amber-800 border-amber-200',
    dotClass: 'bg-amber-500',
  },
  in_progress: {
    label: 'In progress',
    summaryLabel: 'executing',
    chipClass: 'bg-primary-50 text-primary-800 border-primary-200',
    dotClass: 'bg-primary-500',
  },
  error: {
    label: 'Error',
    summaryLabel: 'failed',
    chipClass: 'bg-red-50 text-red-800 border-red-200',
    dotClass: 'bg-red-500',
  },
  done: {
    label: 'Done',
    summaryLabel: 'done',
    chipClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    dotClass: 'bg-emerald-500',
  },
  ran_automatically: {
    label: 'Ran automatically',
    summaryLabel: 'ran automatically',
    chipClass: 'bg-teal-50 text-teal-800 border-teal-200',
    dotClass: 'bg-teal-500',
  },
  abandoned: {
    label: 'Abandoned',
    summaryLabel: 'abandoned',
    chipClass: 'bg-surface-100 text-surface-600 border-surface-300',
    dotClass: 'bg-surface-400',
  },
};

/** Actionable statuses, used in the Review summary line. The Status dropdown
 * lists every status; Default still lands on needs confirmation + error. */
export const PRIMARY_STATUSES: ActivityStatus[] = [
  'needs_confirmation',
  'in_progress',
  'error',
];

/** Landing filter: needs confirmation and error, not in progress. Harsh's
 * zero-inbox default so the Review tab does not open showing every status. */
export const DEFAULT_FILTER_STATUSES: ActivityStatus[] = [
  'needs_confirmation',
  'error',
];

/** Sum of Default-filter buckets. Shared by the Review landing Status option
 * and the tab badge so those two surfaces cannot drift. */
export function defaultFilterCount(
  countsByStatus: Partial<Record<ActivityStatus, number>> | undefined,
): number {
  if (!countsByStatus) return 0;
  return DEFAULT_FILTER_STATUSES.reduce(
    (sum, status) => sum + (countsByStatus[status] ?? 0),
    0,
  );
}

export const SECONDARY_STATUSES: ActivityStatus[] = [
  'done',
  'ran_automatically',
  'abandoned',
];

/** Every status, in the order the Status dropdown lists them and the counts
 * are bucketed. */
export const ALL_STATUSES: ActivityStatus[] = [
  ...PRIMARY_STATUSES,
  ...SECONDARY_STATUSES,
];

/** Statuses the API refuses any further action on, mirroring the domain's
 * `_require_open`. Acting on one comes back 409, so the detail dialog offers
 * no buttons rather than letting them fail. Keep in sync with
 * ai-activity-inbox-api's domain/activity.py. */
export const TERMINAL_STATUSES: ActivityStatus[] = [
  'done',
  'error',
  'abandoned',
  'ran_automatically',
];

/** Statuses where the reviewer cannot act: the terminal ones plus
 * `in_progress`, which is mid-execution. `in_progress` is not finished — the
 * state machine still has to write a terminal status — but the row is no longer
 * the reviewer's, and a second confirm would double-execute. Keep in sync with
 * `_LOCKED_STATUSES` in ai-activity-inbox-api's domain/activity.py. */
export const LOCKED_STATUSES: ActivityStatus[] = [
  ...TERMINAL_STATUSES,
  'in_progress',
];

export interface ActionTypeMeta {
  label: string;
  chipClass: string;
  description: string;
}

export const ACTION_TYPE_META: Record<ActionType, ActionTypeMeta> = {
  create_case: {
    label: 'create_case',
    chipClass: 'bg-primary-100 text-primary-800',
    description: 'Open a new Care Plan case from this conversation.',
  },
  progress_option_status: {
    label: 'progress_option_status',
    chipClass: 'bg-violet-100 text-violet-800',
    description: 'Select or decline a Care Plan option.',
  },
  create_outcome: {
    label: 'create_outcome',
    chipClass: 'bg-teal-100 text-teal-800',
    description: 'Record a Care Plan outcome.',
  },
  calculate_ati: {
    label: 'calculate_ati',
    chipClass: 'bg-cyan-100 text-cyan-800',
    description: "Recalculate the client's Ability to Improve score.",
  },
  create_action: {
    label: 'create_action',
    chipClass: 'bg-amber-100 text-amber-800',
    description: 'Create a Care Plan action for this client.',
  },
  progress_case_status: {
    label: 'progress_case_status',
    chipClass: 'bg-primary-100 text-primary-800',
    description: 'Update an existing Care Plan case status.',
  },
  update_profile: {
    label: 'update_profile',
    chipClass: 'bg-rose-100 text-rose-800',
    description: "Update the client's profile from this conversation.",
  },
  capture_data: {
    label: 'capture_data',
    chipClass: 'bg-primary-100 text-primary-800',
    description: 'Capture structured data from this conversation.',
  },
  send_nps_survey: {
    label: 'send_nps_survey',
    chipClass: 'bg-orange-100 text-orange-800',
    description: 'Send an NPS survey to this client.',
  },
  send_navi_message: {
    label: 'send_navi_message',
    chipClass: 'bg-primary-100 text-primary-800',
    description: 'Send a Navi chat message to this client.',
  },
  send_aifa_message: {
    label: 'send_aifa_message',
    chipClass: 'bg-fuchsia-100 text-fuchsia-800',
    description: 'Send a message via AIFA to this client.',
  },
  broadcast_notification: {
    label: 'broadcast_notification',
    chipClass: 'bg-lime-100 text-lime-800',
    description: 'Broadcast a BrightNav notification.',
  },
  send_email: {
    label: 'send_email',
    chipClass: 'bg-yellow-100 text-yellow-800',
    description: 'Send an email to this client.',
  },
  send_slack_message: {
    label: 'send_slack_message',
    chipClass: 'bg-emerald-100 text-emerald-800',
    description: 'Send a Slack message.',
  },
  send_sms: {
    label: 'send_sms',
    chipClass: 'bg-pink-100 text-pink-800',
    description: 'Send an SMS to this client.',
  },
  create_jira_ticket: {
    label: 'create_jira_ticket',
    chipClass: 'bg-surface-100 text-surface-800',
    description: 'Create a Jira ticket from this conversation.',
  },
  undefined: {
    label: 'undefined',
    chipClass: 'bg-surface-200 text-surface-700',
    description: 'A real task with no typed Care Plan executor.',
  },
};

/** Care Plan order. Dialog chips still use {@link ACTION_TYPE_META.label}
 * (the API verb). The Review Action filter uses {@link ACTION_FILTER_TYPES}. */
export const ACTION_TYPE_ORDER: ActionType[] = [
  'create_case',
  'progress_option_status',
  'create_outcome',
  'calculate_ati',
  'create_action',
  'progress_case_status',
  'update_profile',
  'capture_data',
  'send_nps_survey',
  'send_navi_message',
  'send_aifa_message',
  'broadcast_notification',
  'send_email',
  'send_slack_message',
  'send_sms',
  'create_jira_ticket',
  'undefined',
];

/** Review Action-filter options. `undefined` stays on the admin policy table
 * as the clamp bucket; FAs do not filter on Untyped task. */
export const ACTION_FILTER_TYPES: ActionType[] = ACTION_TYPE_ORDER.filter(
  (type) => type !== 'undefined',
);

/** Human labels for the Review Action filter. Jena thinks in these buckets
 * (new cases vs progressing vs capturing), not snake_case verbs. */
export const ACTION_TYPE_FILTER_LABEL: Record<ActionType, string> = {
  create_case: 'Create case',
  progress_option_status: 'Select or decline option',
  create_outcome: 'Create outcome',
  calculate_ati: 'Calculate ATI',
  create_action: 'Create action',
  progress_case_status: 'Update case status',
  update_profile: 'Update profile',
  capture_data: 'Capture data',
  send_nps_survey: 'Send NPS survey',
  send_navi_message: 'Send Navi message',
  send_aifa_message: 'Send AIFA message',
  broadcast_notification: 'Broadcast notification',
  send_email: 'Send email',
  send_slack_message: 'Send Slack message',
  send_sms: 'Send SMS',
  create_jira_ticket: 'Create Jira ticket',
  undefined: 'Untyped task',
};

export type StatusFilterValue = 'default' | ActivityStatus;
export type ActionTypeFilterValue = 'all' | string;
export type ReviewGroupBy = 'none' | 'date';
export type DateGroupId = 'today' | 'yesterday' | 'older';

const DAY_MS = 86_400_000;

/** Local-calendar bucket for group-by date. Unparseable stamps go in Older. */
export function dateGroupId(
  iso: string | undefined | null,
  nowMs = Date.now(),
): DateGroupId {
  if (!iso) return 'older';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'older';
  const start = new Date(nowMs);
  start.setHours(0, 0, 0, 0);
  const todayStart = start.getTime();
  if (then >= todayStart) return 'today';
  if (then >= todayStart - DAY_MS) return 'yesterday';
  return 'older';
}

export function dateGroupLabel(id: DateGroupId): string {
  switch (id) {
    case 'today':
      return 'Today';
    case 'yesterday':
      return 'Yesterday';
    default:
      return 'Older';
  }
}

export function dateGroupSortIndex(id: DateGroupId): number {
  switch (id) {
    case 'today':
      return 0;
    case 'yesterday':
      return 1;
    default:
      return 2;
  }
}

export function activityHasActionType(
  activity: Activity,
  actionType: string,
): boolean {
  return activity.router_proposal.actions.some(
    (action) => action.action_type === actionType,
  );
}

export function actionTypeFilterLabel(actionType: string): string {
  return ACTION_TYPE_FILTER_LABEL[actionType as ActionType] ?? actionType;
}

const UNKNOWN_ACTION_TYPE_CHIP = 'bg-surface-200 text-surface-700';

/** Chip styling for an action type, tolerating one the UI does not know.
 *
 * `action_type` is an open string on the API side, so this map is no longer
 * exhaustive by construction. The clamp should mean an unknown value never
 * reaches us, but indexing the map directly would blank the chip if one ever
 * did — showing the raw value is a far better failure. Labels here are already
 * the raw snake_case strings, so the fallback needs no prettifying to match.
 *
 * `descriptions` overlays copy from GET /policy when the queue has loaded it,
 * so an operator can change tooltip text without a UI deploy. */
export function actionTypeMeta(
  actionType: string,
  descriptions?: Readonly<Record<string, string>>,
): ActionTypeMeta {
  const known = ACTION_TYPE_META[actionType as ActionType];
  return {
    label: known?.label ?? actionType,
    chipClass: known?.chipClass ?? UNKNOWN_ACTION_TYPE_CHIP,
    description: descriptions?.[actionType] ?? known?.description ?? '',
  };
}

export const DISPOSITION_META: Record<
  Disposition,
  { label: string; chipClass: string }
> = {
  auto_execute: {
    label: 'auto_execute',
    chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  needs_approval: {
    label: 'needs_approval',
    chipClass: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  auto_deny: {
    label: 'auto_deny',
    chipClass: 'bg-surface-100 text-surface-600 border-surface-300',
  },
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "4 min ago" / "5 hrs ago" / "2 days ago", matching the row and modal copy.
 * Returns an empty string for an unparseable timestamp rather than "NaN ago". */
export function relativeTime(iso: string | undefined | null): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';

  const elapsed = Date.now() - then;
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) {
    const mins = Math.floor(elapsed / MINUTE);
    return `${mins} min ago`;
  }
  if (elapsed < DAY) {
    const hrs = Math.floor(elapsed / HOUR);
    return `${hrs} ${hrs === 1 ? 'hr' : 'hrs'} ago`;
  }
  const days = Math.floor(elapsed / DAY);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/** "Aug 14, 2026 at 3:11 PM" in the viewer's locale. Empty if unparseable.
 * Pass `{ seconds: true }` for hover labels that need the exact second. */
export function formatStamp(
  iso: string | undefined | null,
  options?: { seconds?: boolean },
): string {
  if (!iso) return '';
  const when = Date.parse(iso);
  if (Number.isNaN(when)) return '';
  const date = new Date(when);
  const day = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    ...(options?.seconds ? { second: '2-digit' } : {}),
  });
  return `${day} at ${time}`;
}

export function isTerminal(activity: Activity): boolean {
  return TERMINAL_STATUSES.includes(activity.status);
}

export function isLocked(activity: Activity): boolean {
  return LOCKED_STATUSES.includes(activity.status);
}

/** Written per status rather than interpolating STATUS_META.label, which is
 * chip wording and reads wrong in a sentence ("This activity is error"). One
 * entry per locked status. */
const SETTLED_REASON: Partial<Record<ActivityStatus, string>> = {
  done: 'This activity is done — no further action.',
  error: 'This activity failed — no further action.',
  abandoned: 'This activity was denied — no further action.',
  ran_automatically: 'This activity ran automatically — no further action.',
  in_progress: 'Executing — this activity was confirmed and is running.',
};

/** The footer line for an activity the reviewer can no longer act on, or null
 * while it is still theirs. Gated on `isLocked` so a status added to
 * LOCKED_STATUSES without a message here still hides the buttons rather than
 * offering ones that come back 409. */
export function settledReason(activity: Activity): string | null {
  if (!isLocked(activity)) return null;
  return (
    SETTLED_REASON[activity.status] ??
    'This activity is no longer open for review.'
  );
}

/** The quote shown under a row title: the first citation that carries one,
 * falling back to the first message in the window so a proposal with no usable
 * citations still shows what it was reacting to. */
export function triggeringQuote(activity: Activity): string | null {
  const cited = activity.router_proposal.citations.find((citation) =>
    citation.part_body?.trim(),
  );
  if (cited?.part_body) return cited.part_body.trim();
  return activity.enriched_event.message_parts[0]?.body_preview?.trim() ?? null;
}

export interface TriggeringWindowMessage {
  partId: string;
  body: string;
  isTrigger: boolean;
}

/** Cited message plus the chronological neighbor on each side, when they
 * exist. Any author (client, FA, or bot). Usually three messages; two if the
 * trigger is first or last; one if the window has a single part. Falls back
 * to {@link triggeringQuote} when there are no message parts. */
export function triggeringWindow(
  activity: Activity,
): TriggeringWindowMessage[] {
  const parts = activity.enriched_event.message_parts ?? [];
  if (parts.length === 0) {
    const quote = triggeringQuote(activity);
    return quote ? [{ partId: '', body: quote, isTrigger: true }] : [];
  }

  const cited = activity.router_proposal.citations.find(
    (citation) => (citation.part_body ?? '').trim().length > 0,
  );
  let triggerIndex = 0;
  if (cited) {
    const matched = parts.findIndex((part) => part.part_id === cited.part_id);
    triggerIndex = matched >= 0 ? matched : 0;
  }

  const start = Math.max(0, triggerIndex - 1);
  const end = Math.min(parts.length - 1, triggerIndex + 1);
  const messages: TriggeringWindowMessage[] = [];
  for (let i = start; i <= end; i++) {
    const part = parts[i];
    const isTrigger = i === triggerIndex;
    const citationBody = isTrigger
      ? (
          activity.router_proposal.citations.find(
            (citation) => citation.part_id === part.part_id,
          )?.part_body ?? ''
        ).trim()
      : '';
    const body = (citationBody || part.body_preview || '').trim();
    if (!body) continue;
    messages.push({ partId: part.part_id, body, isTrigger });
  }
  return messages;
}

export function actionCountLabel(activity: Activity): string {
  const n = activity.router_proposal.actions.length;
  return `${n} proposed ${n === 1 ? 'action' : 'actions'}`;
}

/** Digit on the activity row. Full phrase is the hover tooltip. */
export function actionCountDigit(activity: Activity): string {
  return String(activity.router_proposal.actions.length);
}

/** Action titles for the activity row, in proposal order. Falls back to
 * `action_type` if a title is blank so the numbered list never has a hole. */
export function actionPreviewTitles(activity: Activity): string[] | null {
  const titles = activity.router_proposal.actions.map(
    (proposed) => proposed.title.trim() || proposed.action_type,
  );
  return titles.length === 0 ? null : titles;
}

export function confirmExecutingToast(title: string): string {
  return `Now executing, ${title}`;
}

/** "1 auto_execute · 2 needs_approval" for the proposed-actions header, listing
 * only the dispositions actually present. */
export function dispositionBreakdown(activity: Activity): string {
  const counts = new Map<Disposition, number>();
  for (const action of activity.router_proposal.actions) {
    counts.set(action.disposition, (counts.get(action.disposition) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([disposition, count]) => `${count} ${disposition}`)
    .join(' · ');
}
